import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import { useAuth } from './AuthContext';
import LandingFooter from '../Landing/LandingFooter';
import './StudentProfile.css';

const StudentProfile = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    address: '',
    phone_number: '',
    whatsapp_number: '',
    parent_name: '',
    parent_number: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // If not logged in at all, kick them back to home
    if (user === null) {
        navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || user?.user_metadata?.full_name || '',
        address: profile.address || '',
        phone_number: profile.phone_number || '',
        whatsapp_number: profile.whatsapp_number || '',
        parent_name: profile.parent_name || '',
        parent_number: profile.parent_number || ''
      });
      
      // If profile is completely empty, default to edit mode
      if (!profile.phone_number && !profile.address) {
        setIsEditing(true);
      }
    }
  }, [profile, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    
    setSaving(true);
    setMessage(null);

    const { error } = await supabase
      .from('profiles')
      .update({
        full_name: formData.full_name,
        address: formData.address,
        phone_number: formData.phone_number,
        whatsapp_number: formData.whatsapp_number,
        parent_name: formData.parent_name,
        parent_number: formData.parent_number
      })
      .eq('id', user.id);

    if (error) {
      setMessage({ type: 'error', text: 'Error saving profile: ' + error.message });
      setSaving(false);
    } else {
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setSaving(false);
      setTimeout(() => {
        setIsEditing(false);
        setMessage(null);
      }, 1500);
    }
  };

  if (!user || !profile) {
    return (
      <div style={{ textAlign: 'center', padding: '100px', color: '#fff', background: '#0f0f1a', minHeight: '100vh' }}>
        <h3>Loading Profile...</h3>
      </div>
    );
  }

  const avatarUrl = user.user_metadata?.avatar_url || user.user_metadata?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.user_metadata?.full_name || 'User')}&background=random`;

  return (
    <div className="profile-container">
      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-wrapper">
              <img src={avatarUrl} alt="Profile" className="profile-avatar" />
            </div>
            
            {!isEditing && (
              <div className="profile-header-actions">
                <button className="edit-btn" onClick={() => setIsEditing(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                  Edit Profile
                </button>
              </div>
            )}
          </div>

          <div className="profile-body">
            {!isEditing ? (
              // View Mode
              <div>
                <h2 className="profile-name">{profile.full_name || user?.user_metadata?.full_name || 'User'}</h2>
                <p className="profile-email">{user.email}</p>
                
                <div className="profile-grid">
                  <div className="info-item">
                    <div className="info-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                      Phone Number
                    </div>
                    <div className={profile.phone_number ? "info-value" : "info-empty"}>
                      {profile.phone_number || 'Not provided'}
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                      WhatsApp
                    </div>
                    <div className={profile.whatsapp_number ? "info-value" : "info-empty"}>
                      {profile.whatsapp_number || 'Not provided'}
                    </div>
                  </div>
                  
                  <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                    <div className="info-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                      Home Address
                    </div>
                    <div className={profile.address ? "info-value" : "info-empty"}>
                      {profile.address || 'Not provided'}
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                      Parent / Guardian
                    </div>
                    <div className={profile.parent_name ? "info-value" : "info-empty"}>
                      {profile.parent_name || 'Not provided'}
                    </div>
                  </div>
                  
                  <div className="info-item">
                    <div className="info-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                      Parent Number
                    </div>
                    <div className={profile.parent_number ? "info-value" : "info-empty"}>
                      {profile.parent_number || 'Not provided'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Edit Mode
              <div className="edit-form-container">
                <h2 style={{ marginBottom: '24px', fontSize: '1.8rem', color: '#fff' }}>Edit Profile</h2>
                
                <form onSubmit={handleSubmit}>
                  {message && (
                    <div style={{ 
                      padding: '12px 16px', 
                      borderRadius: '8px', 
                      marginBottom: '24px',
                      background: message.type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                      border: `1px solid ${message.type === 'error' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
                      color: message.type === 'error' ? '#fca5a5' : '#86efac'
                    }}>
                      {message.text}
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      placeholder="Your complete name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Home Address</label>
                    <textarea
                      required
                      rows="2"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="Your full residential address"
                    ></textarea>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Mobile Number (Tp no)</label>
                      <input
                        type="tel"
                        required
                        value={formData.phone_number}
                        onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                        placeholder="07XXXXXXXX"
                      />
                    </div>
                    <div className="form-group">
                      <label>WhatsApp Number</label>
                      <input
                        type="tel"
                        required
                        value={formData.whatsapp_number}
                        onChange={(e) => setFormData({...formData, whatsapp_number: e.target.value})}
                        placeholder="07XXXXXXXX"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Parent / Guardian Name</label>
                      <input
                        type="text"
                        required
                        value={formData.parent_name}
                        onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
                        placeholder="Name of parent or guardian"
                      />
                    </div>
                    <div className="form-group">
                      <label>Parent Mobile Number</label>
                      <input
                        type="tel"
                        required
                        value={formData.parent_number}
                        onChange={(e) => setFormData({...formData, parent_number: e.target.value})}
                        placeholder="07XXXXXXXX"
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-primary" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button 
                      type="button" 
                      className="btn-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        setMessage(null);
                        // Reset form to current profile state if canceled
                        setFormData({
                          full_name: profile.full_name || user?.user_metadata?.full_name || '',
                          address: profile.address || '',
                          phone_number: profile.phone_number || '',
                          whatsapp_number: profile.whatsapp_number || '',
                          parent_name: profile.parent_name || '',
                          parent_number: profile.parent_number || ''
                        });
                      }} 
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default StudentProfile;
